import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';

@Component({
  selector: 'app-notfound',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './notfound.component.html',
  styleUrl: './notfound.component.scss'
})
export class NotFoundComponent implements OnInit {
  errorType: string | null = null;
  errorCode: string | null = null;
  errorMessage: string | null = null;
  isSpecificError = false;

  // Computed display properties
  title: string = '';
  icon: string = '';
  description: string = '';
  safeDescription: SafeHtml = '';
  specificActions: { label: string, icon: string, action: () => void }[] = [];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    // Read query parameters from snapshot (no subscription needed)
    const params = this.route.snapshot.queryParams;
    this.errorType = params['type'] || null;
    this.errorCode = params['code'] || null;
    this.errorMessage = params['message'] || null;
    this.isSpecificError = !!(this.errorType || this.errorCode || this.errorMessage);

    // Compute display properties once
    this.title = this.calculateTitle();
    this.icon = this.calculateIcon();
    this.description = this.calculateDescription();
    this.safeDescription = this.sanitizer.sanitize(
      SecurityContext.HTML,
      this.description
    ) || '';
    this.specificActions = this.calculateSpecificActions();
  }

  private calculateTitle(): string {
    if (this.errorType === 'matter') {
      return 'Matter Not Found';
    }
    if (this.errorType === 'foreign-national') {
      return 'Foreign National Not Found';
    }
    if (this.errorType === 'company') {
      return 'Company Not Found';
    }
    return 'Page Not Found';
  }

  private calculateIcon(): string {
    if (this.errorType === 'matter') {
      return 'pi pi-pen-to-square';
    }
    if (this.errorType === 'foreign-national') {
      return 'pi pi-user';
    }
    if (this.errorType === 'company') {
      return 'pi pi-building';
    }
    return 'pi pi-search';
  }

  private calculateDescription(): string {
    if (this.isSpecificError) {
      if (this.errorMessage) {
        return this.errorMessage;
      }
      if (this.errorType === 'matter' && this.errorCode) {
        return `The matter with code "${this.errorCode}" could not be found. It may have been removed or the code may be incorrect.`;
      }
      if (this.errorType === 'foreign-national' && this.errorCode) {
        return `The foreign national with code "${this.errorCode}" could not be found. It may have been removed or the code may be incorrect.`;
      }
      if (this.errorType === 'company' && this.errorCode) {
        return `The company with code "${this.errorCode}" could not be found. It may have been removed or the code may be incorrect.`;
      }
    }
    return 'Sorry, the page you are looking for doesn\'t exist or has been moved.<br>Please check the URL or use the buttons below to navigate elsewhere.';
  }

  private calculateSpecificActions(): { label: string, icon: string, action: () => void }[] {
    const actions: { label: string, icon: string, action: () => void }[] = [];

    if (this.errorType === 'matter') {
      actions.push({
        label: 'Browse Matters',
        icon: 'pi pi-list',
        action: () => this.router.navigate(['/matter'])
      });
    }

    if (this.errorType === 'foreign-national') {
      actions.push({
        label: 'Browse Foreign Nationals',
        icon: 'pi pi-list',
        action: () => this.router.navigate(['/foreign-national'])
      });
    }

    if (this.errorType === 'company') {
      actions.push({
        label: 'Browse Companies',
        icon: 'pi pi-list',
        action: () => this.router.navigate(['/company'])
      });
    }

    return actions;
  }

  goHome() {
    this.router.navigate(['/']);
  }
}
